/**
 * Asking a peer whether its game is still running, rather than whether its cable is still plugged
 * in.
 *
 * [peerHealth](../rtc/peerHealth.ts) watches the `RTCPeerConnection`, which is the right thing to
 * watch for a laptop that shut its lid: the connection notices and says so. It cannot see the other
 * failure, a tab that is throttled to a stop, a page frozen on a breakpoint or a loop that threw.
 * There the connection is open, the channels are open, and nothing will ever arrive again. Only the
 * application can tell, and only by asking.
 *
 * So one ping per peer per second, on the reliable channel, answered with the stamp handed straight
 * back. Two things fall out of it. Silence for five beats is a peer that has stopped, reported the
 * same way a dead connection is, comfortably before the eight second grace the connection watcher
 * would eventually spend. And the echo is a round trip measured against one clock, which is the
 * only honest way to measure one between two machines that have never agreed what time it is.
 *
 * Pure: the clock, the timer and the sending are all injected, so a whole session's worth of beats
 * runs in a test in no time at all.
 */

import { decodeHeartbeat, encodeHeartbeat } from '@/net/wire/heartbeatFrame';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import type { MessageKind } from '@/net/wire/messageKinds';
import type { PeerId } from '@/net/transportContract';

/** How often a peer is asked. Human cadence: eight bytes a second either way, framing included. */
export const HEARTBEAT_EVERY_MS = 1000;

/**
 * How long a peer may say nothing at all before it is treated as stopped.
 *
 * Five beats, so a couple lost to a hiccup cost nothing, and still well inside the eight second
 * `RECOVERY_GRACE_MS` the connection watcher spends, so whichever failure happened the player waits
 * once rather than twice.
 */
export const HEARTBEAT_SILENCE_MS = 5000;

export interface PeerHeartbeatOptions {
  /** Local milliseconds. Never sent as a point in time, only as a stamp to hand back. */
  readonly nowMs: () => number;
  readonly repeat: (everyMs: number, run: () => void) => () => void;
  /** One frame to one peer, reliably. The transport does the framing, as everything else does. */
  readonly send: (to: PeerId, kind: MessageKind, payload: Uint8Array) => void;
  /** This peer has said nothing for `HEARTBEAT_SILENCE_MS`. Reported once, then forgotten. */
  readonly onSilent: (id: PeerId, reason: string) => void;
  /** A fresh round trip for a peer, in milliseconds. */
  readonly onRtt: (id: PeerId, rttMs: number) => void;
}

export interface PeerHeartbeat {
  /** Starts beating at a peer. Its silence is counted from now, not from its first answer. */
  addPeer(id: PeerId): void;
  removePeer(id: PeerId): void;
  /** A ping off the wire, answered with the sender's own stamp, untouched. */
  receivePing(from: PeerId, payload: Uint8Array): void;
  receivePong(from: PeerId, payload: Uint8Array): void;
  /** The last round trip measured for a peer, or null before it has answered anything. */
  rttOf(id: PeerId): number | null;
  stop(): void;
}

interface PeerBeat {
  /** When anything at all last arrived from this peer. A ping counts: it proves a running loop. */
  heardAtMs: number;
  rttMs: number | null;
}

export function createPeerHeartbeat(options: PeerHeartbeatOptions): PeerHeartbeat {
  const beats = new Map<PeerId, PeerBeat>();

  const beat = (): void => {
    const nowMs = options.nowMs();
    for (const [id, peer] of [...beats]) {
      if (nowMs - peer.heardAtMs >= HEARTBEAT_SILENCE_MS) {
        beats.delete(id);
        options.onSilent(id, 'the peer stopped answering');
        continue;
      }
      options.send(id, MESSAGE_KINDS.ping, encodeHeartbeat(nowMs));
    }
  };

  const stopBeating = options.repeat(HEARTBEAT_EVERY_MS, beat);

  /** Anything arriving from a peer is proof it is running, whatever it happened to be. */
  const heard = (from: PeerId): PeerBeat | null => {
    const peer = beats.get(from);
    if (peer === undefined) return null;
    peer.heardAtMs = options.nowMs();
    return peer;
  };

  return {
    addPeer(id: PeerId): void {
      if (beats.has(id)) return;
      beats.set(id, { heardAtMs: options.nowMs(), rttMs: null });
    },

    removePeer(id: PeerId): void {
      beats.delete(id);
    },

    receivePing(from: PeerId, payload: Uint8Array): void {
      if (heard(from) === null) return;
      options.send(from, MESSAGE_KINDS.pong, payload);
    },

    receivePong(from: PeerId, payload: Uint8Array): void {
      const peer = heard(from);
      if (peer === null) return;

      // Subtracted in the same 32 bits the stamp was written in, so the wrap every 49 days is a
      // round trip like any other rather than a peer that answered seven weeks early.
      const rttMs = ((Math.trunc(options.nowMs()) >>> 0) - decodeHeartbeat(payload)) >>> 0;
      peer.rttMs = rttMs;
      options.onRtt(from, rttMs);
    },

    rttOf: (id: PeerId): number | null => beats.get(id)?.rttMs ?? null,

    stop(): void {
      stopBeating();
      beats.clear();
    },
  };
}
